/**
 * TMS Lane Panel — the customer's separate TMS subscription (ADR-020): paid, trial, or
 * Ultra-perk comp. Operator actions: grant/extend a TMS trial, cancel TMS.
 */
import React, { useState } from 'react'
import { LocalShipping, Warning, CardGiftcard, Edit } from '@mui/icons-material'
import dayjs from 'dayjs'
import { TmsSubscriptionSource } from '@numeo/types-core'
import { billingApi } from './api'
import { TmsSubscriptionInfo } from './types'
import AdminActionDialog from './AdminActionDialog'
import GiveTrialDialog from './GiveTrialDialog'

interface TmsLanePanelProps {
    appId: string
    tmsSubscription: TmsSubscriptionInfo | null
    onSuccess: (message: string) => void
    onError: (message: string) => void
    onRefresh: () => Promise<void>
}

function sourceLabel(source: TmsSubscriptionSource): string {
    if (source === 'ultra_perk') return 'Ultra perk (comp)'
    if (source === 'trial') return 'Trial'
    return 'Paid'
}

function sourceBadgeClasses(source: TmsSubscriptionSource): string {
    if (source === 'ultra_perk') return 'bg-purple-50 text-purple-700'
    if (source === 'trial') return 'bg-amber-50 text-amber-700'
    return 'bg-emerald-50 text-emerald-700'
}

function statusBadgeClasses(status: string): string {
    if (status === 'active') return 'bg-emerald-50 text-emerald-700'
    if (status === 'trialing') return 'bg-amber-50 text-amber-700'
    return 'bg-red-50 text-red-700'
}

export default function TmsLanePanel({ appId, tmsSubscription, onSuccess, onError, onRefresh }: TmsLanePanelProps) {
    const tms = tmsSubscription
    const [trialDialogOpen, setTrialDialogOpen] = useState(false)
    const [cancelDialogOpen, setCancelDialogOpen] = useState(false)
    const [cancelLoading, setCancelLoading] = useState(false)
    const [editingTrucks, setEditingTrucks] = useState(false)
    const [trucksInput, setTrucksInput] = useState('')
    const [trucksSaving, setTrucksSaving] = useState(false)

    // Trial-only: the seat guard hard-blocks truck-adds while trialing, so operators bump
    // capacity here; paid subs change quantity through the product billing page (proration).
    const canEditTrucks = tms?.status === 'trialing' && !!tms?.stripeSubscriptionId

    const handleSaveTrucks = async () => {
        const count = Number(trucksInput)
        if (!Number.isInteger(count) || count < 1) {
            onError('Trucks must be a positive integer')
            return
        }
        setTrucksSaving(true)
        try {
            await billingApi.setTmsTrucks(appId, { trucks: count })
            setEditingTrucks(false)
            onSuccess(`TMS truck capacity set to ${count}`)
            await onRefresh()
        } catch (err: unknown) {
            const axiosErr = err as { response?: { data?: { message?: string } } }
            onError(axiosErr.response?.data?.message || 'Failed to update truck count')
        } finally {
            setTrucksSaving(false)
        }
    }

    const handleCancelTms = async (reason: string) => {
        setCancelLoading(true)
        try {
            const result = await billingApi.cancelTms(appId, { reason })
            setCancelDialogOpen(false)
            onSuccess(result.effectiveDate ? `TMS subscription will end ${dayjs(result.effectiveDate).format('MMM D, YYYY')}` : 'TMS subscription cancelled')
            await onRefresh()
        } catch (err: unknown) {
            const axiosErr = err as { response?: { data?: { message?: string } } }
            onError(axiosErr.response?.data?.message || 'Failed to cancel TMS subscription')
        } finally {
            setCancelLoading(false)
        }
    }

    return (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm mb-6">
            <div className="px-5 py-3 border-b border-gray-100 flex items-center gap-2">
                <div className="w-7 h-7 rounded-md bg-cyan-50 flex items-center justify-center">
                    <LocalShipping sx={{ fontSize: 14 }} className="text-cyan-700" />
                </div>
                <div>
                    <p className="text-sm font-semibold text-gray-900">TMS Subscription</p>
                    <p className="text-xs text-gray-400">Separate per-truck TMS lane — paid, trial, or Ultra comp</p>
                </div>
            </div>
            <div className="p-5">
                {tms ? (
                    <>
                        <div className="grid grid-cols-4 gap-4 mb-4">
                            <div className="bg-gray-50 rounded-lg p-3">
                                <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">Tier</p>
                                <span className="text-xs font-bold px-2.5 py-0.5 rounded bg-cyan-100 text-cyan-800 uppercase">{tms.tier}</span>
                            </div>
                            <div className="bg-gray-50 rounded-lg p-3">
                                <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">Status</p>
                                <span className={`text-xs px-2.5 py-0.5 rounded ${statusBadgeClasses(tms.status)}`}>{tms.status}</span>
                            </div>
                            <div className="bg-gray-50 rounded-lg p-3">
                                <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">Source</p>
                                <span className={`text-xs px-2.5 py-0.5 rounded ${sourceBadgeClasses(tms.source)}`}>{sourceLabel(tms.source)}</span>
                            </div>
                            <div className="bg-gray-50 rounded-lg p-3">
                                <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">Trucks</p>
                                {editingTrucks ? (
                                    <div className="flex items-center gap-1.5">
                                        <input
                                            type="number"
                                            min={1}
                                            value={trucksInput}
                                            onChange={(e) => setTrucksInput(e.target.value)}
                                            disabled={trucksSaving}
                                            className="w-16 h-7 px-2 text-sm border border-gray-200 rounded-md"
                                        />
                                        <button
                                            onClick={handleSaveTrucks}
                                            disabled={trucksSaving}
                                            type="button"
                                            className="h-7 px-2 text-xs font-medium bg-cyan-700 text-white rounded-md hover:bg-cyan-800 disabled:opacity-50"
                                        >
                                            {trucksSaving ? '…' : 'Save'}
                                        </button>
                                        <button
                                            onClick={() => setEditingTrucks(false)}
                                            disabled={trucksSaving}
                                            type="button"
                                            className="h-7 px-1.5 text-xs text-gray-500 hover:text-gray-700"
                                        >
                                            ✕
                                        </button>
                                    </div>
                                ) : (
                                    <p className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                                        {tms.purchasedTrucks ?? '—'}
                                        {tms.billingInterval ? <span className="text-xs text-gray-400 font-normal"> · {tms.billingInterval}</span> : null}
                                        {canEditTrucks && (
                                            <button
                                                onClick={() => {
                                                    setTrucksInput(String(tms.purchasedTrucks ?? 10))
                                                    setEditingTrucks(true)
                                                }}
                                                type="button"
                                                title="Update truck capacity (trial hard-blocks truck-adds beyond it)"
                                                className="text-cyan-700 hover:text-cyan-900"
                                            >
                                                <Edit sx={{ fontSize: 13 }} />
                                            </button>
                                        )}
                                    </p>
                                )}
                            </div>
                        </div>
                        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 pt-3 border-t border-gray-100 text-xs text-gray-600">
                            {tms.trialEnd && (
                                <span>
                                    <span className="text-gray-400 font-semibold uppercase text-[10px] tracking-wider mr-1.5">Trial End</span>
                                    {dayjs(tms.trialEnd).format('MMM D, YYYY')}
                                </span>
                            )}
                            {tms.currentPeriodEnd && (
                                <span>
                                    <span className="text-gray-400 font-semibold uppercase text-[10px] tracking-wider mr-1.5">Period End</span>
                                    {dayjs(tms.currentPeriodEnd).format('MMM D, YYYY')}
                                </span>
                            )}
                            {tms.perkExpiresAt && (
                                <span>
                                    <span className="text-gray-400 font-semibold uppercase text-[10px] tracking-wider mr-1.5">Perk Expires</span>
                                    {dayjs(tms.perkExpiresAt).format('MMM D, YYYY')}
                                </span>
                            )}
                            {tms.stripeSubscriptionId && <span className="font-mono text-[10px] text-gray-400">{tms.stripeSubscriptionId}</span>}
                            {tms.cancelAtPeriodEnd && <span className="text-amber-600 bg-amber-50 px-2 py-0.5 rounded">Will cancel at period end</span>}
                        </div>
                    </>
                ) : (
                    <div className="text-center py-6">
                        <LocalShipping sx={{ fontSize: 28 }} className="text-gray-300 mb-2" />
                        <p className="text-sm text-gray-500 mb-1">No TMS subscription</p>
                        <p className="text-xs text-gray-400">This customer has no paid TMS plan, trial, or Ultra comp</p>
                    </div>
                )}

                {/* Actions */}
                <div className="mt-4 pt-4 border-t border-gray-100 flex flex-wrap gap-2">
                    <button
                        onClick={() => setTrialDialogOpen(true)}
                        className="h-8 px-4 text-xs font-medium bg-emerald-600 text-white rounded-md hover:bg-emerald-700 transition-colors flex items-center gap-1.5"
                    >
                        <CardGiftcard sx={{ fontSize: 12 }} /> Give TMS Trial
                    </button>
                    {tms && !tms.cancelAtPeriodEnd && (
                        <button
                            onClick={() => setCancelDialogOpen(true)}
                            className="h-8 px-3 text-xs text-red-600 border border-red-200 rounded-md hover:bg-red-50 flex items-center gap-1.5 transition-colors"
                        >
                            <Warning sx={{ fontSize: 12 }} /> Cancel TMS
                        </button>
                    )}
                </div>
            </div>

            <GiveTrialDialog
                open={trialDialogOpen}
                lane="tms"
                appId={appId}
                tmsSubscription={tms}
                onClose={() => setTrialDialogOpen(false)}
                onSuccess={onSuccess}
                onRefresh={onRefresh}
            />
            <AdminActionDialog
                open={cancelDialogOpen}
                title="Cancel TMS Subscription"
                description="Trials are cancelled immediately; paid TMS subscriptions cancel at the end of the current billing period."
                severity="warning"
                confirmLabel="Cancel TMS"
                loading={cancelLoading}
                onConfirm={(reason) => void handleCancelTms(reason)}
                onClose={() => setCancelDialogOpen(false)}
            />
        </div>
    )
}
